/**
 * String similarity utilities for duplicate detection
 * Catches variations in company names (spaces, hyphens, abbreviations)
 */

/**
 * Normalize a string for comparison
 * Handles variations in spacing, punctuation, and common substitutions
 */
export const normalizeString = (str) => {
  if (!str) return '';
  
  let normalized = str
    .toLowerCase()
    .trim();
  
  // Replace common word-number equivalents
  const wordToNumber = {
    'seven': '7',
    'eleven': '11',
    'one': '1',
    'two': '2',
    'three': '3',
    'four': '4',
    'five': '5',
    'six': '6',
    'eight': '8',
    'nine': '9',
    'ten': '10',
    'twenty': '20',
    'thirty': '30',
    'forty': '40',
    'fifty': '50'
  };
  
  Object.entries(wordToNumber).forEach(([word, num]) => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    normalized = normalized.replace(regex, num);
  });
  
  // Remove common business suffixes
  normalized = normalized
    .replace(/\s*(inc|llc|corp|corporation|ltd|limited|co|company)\s*\.?$/gi, '');
  
  // Remove all spaces, hyphens, underscores, dots, apostrophes
  normalized = normalized.replace(/[\s-_.'']/g, '');
  
  // Replace & with 'and'
  normalized = normalized.replace(/&/g, 'and');
  
  return normalized;
};

/**
 * Calculate Levenshtein distance between two strings
 * Returns number of edits needed to transform one string to another
 */
export const levenshteinDistance = (str1, str2) => {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1.charAt(i - 1) === str2.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[len1][len2];
};

/**
 * Calculate similarity percentage between two strings
 * Returns 0-100 (100 = identical, 0 = completely different)
 */
export const calculateSimilarity = (str1, str2) => {
  const normalized1 = normalizeString(str1);
  const normalized2 = normalizeString(str2);
  
  if (!normalized1 || !normalized2) return 0;
  if (normalized1 === normalized2) return 100;
  
  const distance = levenshteinDistance(normalized1, normalized2);
  const maxLength = Math.max(normalized1.length, normalized2.length);
  
  if (maxLength === 0) return 100;
  
  const similarity = ((maxLength - distance) / maxLength) * 100;
  return Math.round(similarity);
};

/**
 * Check if string contains another (normalized)
 * Returns true if str1 contains str2 or vice versa
 */
export const containsNormalized = (str1, str2) => {
  const normalized1 = normalizeString(str1);
  const normalized2 = normalizeString(str2);
  
  return normalized1.includes(normalized2) || normalized2.includes(normalized1);
};

/**
 * Find similar company names from a list
 * Returns companies with similarity >= threshold
 */
export const findSimilarCompanies = (inputName, companies, threshold = 70) => {
  if (!inputName || inputName.length < 3) return [];
  
  const results = companies
    .map(company => {
      const companyName = company.client_name || company.company_name || '';
      const similarity = calculateSimilarity(inputName, companyName);
      const contains = containsNormalized(inputName, companyName);
      
      return {
        company,
        similarity,
        contains,
        score: contains ? 100 : similarity // Boost score if one contains the other
      };
    })
    .filter(result => result.score >= threshold)
    .sort((a, b) => b.score - a.score);
  
  return results;
};

/**
 * Get similarity description for UI
 */
export const getSimilarityDescription = (similarity) => {
  if (similarity === 100) return 'Exact match';
  if (similarity >= 90) return 'Very similar';
  if (similarity >= 80) return 'Similar';
  if (similarity >= 70) return 'Possibly related';
  return 'Might be similar';
};
